import React, { useState } from 'react';
import { Phone, Video, MessageSquare, Calendar, CheckCircle2, Award, Shield, ArrowRight, ChevronDown } from 'lucide-react';
import { AnimatedGridPattern } from '../components/ui/AnimatedGridPattern';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
interface BeratungPageProps {
  onNavigate: (page: string) => void;
}
export function BeratungPage({
  onNavigate
}: BeratungPageProps) {
  const [formData, setFormData] = useState({
    honeypot: '',
    thema: '',
    plz: '',
    name: '',
    email: '',
    telefon: '',
    nachricht: '',
    einwilligung: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const themenOptions = ['Allgemeine Versicherungsanalyse', 'Bestehende Verträge prüfen', 'Privat: Haftpflicht / Hausrat / Rechtsschutz', 'Immobilie: Gebäude / Eigentümer / Vermietung', 'Gewerbe / Selbstständig', 'Kranken: Zusatz / PKV-Fragen', 'Kfz / Mobilität', 'Sonstiges'];
  const supportPoints = ['Wir ordnen Tarife und Bedingungen verständlich ein.', 'Wir zeigen typische Stolperfallen.', 'Du bekommst Empfehlungen passend zu deiner Situation – ohne Druck.', 'Persönliche Beratung ist jederzeit möglich – du entscheidest, wie viel Unterstützung du willst.'];
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.honeypot) return; // Bot protection
    setIsSubmitting(true);
    try {
      const response = await fetch('https://formsubmit.co/ajax/info@axentradvisory.de', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          Thema: formData.thema,
          PLZ: formData.plz,
          Name: formData.name,
          Email: formData.email,
          Telefon: formData.telefon || 'Nicht angegeben',
          Nachricht: formData.nachricht || 'Keine Nachricht',
          _subject: `Neue Beratungsanfrage: ${formData.thema || 'Allgemein'}`
        })
      });
      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          honeypot: '',
          thema: '',
          plz: '',
          name: '',
          email: '',
          telefon: '',
          nachricht: '',
          einwilligung: false
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (submitSuccess) {
    return <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300" data-id="element-422">
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-sky-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-blue-950/20 pt-24 pb-12 lg:pt-32 lg:pb-16 border-b border-slate-200 dark:border-slate-800" data-id="element-423">
          <AnimatedGridPattern numSquares={20} maxOpacity={0.1} duration={3} repeatDelay={1} className="[mask-image:radial-gradient(500px_circle_at_center,white,transparent)] inset-x-0 inset-y-[-30%] h-[200%] skew-y-12" data-id="element-424" />
          <div className="container mx-auto px-4 text-center relative z-10 max-w-3xl" data-id="element-425">
            <div className="flex justify-center mb-6" data-id="element-426">
              <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-427">
                <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" data-id="element-428" />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight" data-id="element-429">
              Anfrage gesendet!
            </h1>
            <p className="text-base text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed mb-6" data-id="element-430">
              Vielen Dank für deine Nachricht. Wir melden uns schnellstmöglich
              bei dir.
            </p>
            <Button onClick={() => setSubmitSuccess(false)} variant="outline" data-id="element-431">
              Neue Anfrage stellen
            </Button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300" data-id="element-432">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-sky-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-blue-950/20 pt-24 pb-12 lg:pt-32 lg:pb-16 border-b border-slate-200 dark:border-slate-800" data-id="element-433">
        <AnimatedGridPattern numSquares={20} maxOpacity={0.1} duration={3} repeatDelay={1} className="[mask-image:radial-gradient(500px_circle_at_center,white,transparent)] inset-x-0 inset-y-[-30%] h-[200%] skew-y-12" data-id="element-434" />

        <div className="container mx-auto px-4 text-center relative z-10 max-w-3xl" data-id="element-435">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight" data-id="element-436">
            Persönliche Beratung
          </h1>
          <p className="text-base text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed" data-id="element-437">
            Du kannst jederzeit persönliche Beratung dazunehmen – telefonisch
            oder digital. Schreib kurz, worum es geht.
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-3 max-w-xl mx-auto" data-id="element-438">
            Besonders wichtig bei sensiblen Themen: Wir klären das sauber
            gemeinsam – Schritt für Schritt.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 lg:py-16" data-id="element-439">
        <div className="max-w-4xl mx-auto grid lg:grid-cols-5 gap-10" data-id="element-440">
          {/* Contact Form */}
          <div className="lg:col-span-3" data-id="element-441">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6" data-id="element-442">
              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-443">
                {/* Honeypot */}
                <input type="text" name="honeypot" value={formData.honeypot} onChange={e => setFormData({
                ...formData,
                honeypot: e.target.value
              })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" data-id="element-444" />

                {/* Thema Select */}
                <div data-id="element-445">
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5" data-id="element-446">
                    Thema
                  </label>
                  <div className="relative" data-id="element-447">
                    <select value={formData.thema} onChange={e => setFormData({
                    ...formData,
                    thema: e.target.value
                  })} className="w-full h-10 px-3 pr-10 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-448">
                      <option value="" data-id="element-449">Bitte auswählen…</option>
                      {themenOptions.map(option => <option key={option} value={option} data-id="element-450">
                          {option}
                        </option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-451" />
                  </div>
                </div>

                {/* PLZ & Name Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-452">
                  <div data-id="element-453">
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5" data-id="element-454">
                      PLZ
                    </label>
                    <input type="text" value={formData.plz} onChange={e => setFormData({
                    ...formData,
                    plz: e.target.value
                  })} className="w-full h-10 px-3 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="z.B. 10115" data-id="element-455" />
                  </div>
                  <div data-id="element-456">
                    <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5" data-id="element-457">
                      Name
                    </label>
                    <input type="text" value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} className="w-full h-10 px-3 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required data-id="element-458" />
                  </div>
                </div>

                {/* Email */}
                <div data-id="element-459">
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5" data-id="element-460">
                    E-Mail
                  </label>
                  <input type="email" value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="w-full h-10 px-3 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required data-id="element-461" />
                </div>

                {/* Telefon */}
                <div data-id="element-462">
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5" data-id="element-463">
                    Telefon{' '}
                    <span className="text-slate-400 font-normal" data-id="element-464">
                      (optional)
                    </span>
                  </label>
                  <input type="tel" value={formData.telefon} onChange={e => setFormData({
                  ...formData,
                  telefon: e.target.value
                })} className="w-full h-10 px-3 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-465" />
                </div>

                {/* Nachricht */}
                <div data-id="element-466">
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5" data-id="element-467">
                    Worum geht's bei dir?{' '}
                    <span className="text-slate-400 font-normal" data-id="element-468">
                      (optional)
                    </span>
                  </label>
                  <textarea value={formData.nachricht} onChange={e => setFormData({
                  ...formData,
                  nachricht: e.target.value
                })} rows={3} className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" data-id="element-469" />
                </div>

                {/* Einwilligung */}
                <div className="flex items-start gap-2.5" data-id="element-470">
                  <input type="checkbox" id="einwilligung" checked={formData.einwilligung} onChange={e => setFormData({
                  ...formData,
                  einwilligung: e.target.checked
                })} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" required data-id="element-471" />
                  <label htmlFor="einwilligung" className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-472">
                    Ich bin einverstanden, dass smartprotect24 meine Angaben zur
                    Kontaktaufnahme und zur Beratung verarbeitet.
                  </label>
                </div>

                {/* Submit */}
                <Button type="submit" variant="premium" className="w-full mt-2" disabled={isSubmitting} data-id="element-473">
                  {isSubmitting ? 'Wird gesendet...' : 'Anfrage senden'}
                </Button>

                {/* Hinweise */}
                <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2" data-id="element-474">
                  <p className="text-[11px] text-slate-500 dark:text-slate-500 leading-relaxed" data-id="element-475">
                    <strong data-id="element-476">Hinweis:</strong> smartprotect24 ist ein
                    Versicherungsanalyse- und Vermittlungsservice
                    (Versicherungsmakler). Angaben ohne Gewähr. Keine Steuer-
                    oder Rechtsberatung.
                  </p>
                </div>
              </form>
            </div>
          </div>

          {/* So unterstützen wir dich */}
          <div className="lg:col-span-2" data-id="element-477">
            <div className="sticky top-24" data-id="element-478">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4" data-id="element-479">
                So unterstützen wir dich
              </h2>
              <ul className="space-y-3" data-id="element-480">
                {supportPoints.map((point, i) => <li key={i} className="flex items-start gap-2.5" data-id="element-481">
                    <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" data-id="element-482" />
                    <span className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-483">
                      {point}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>;
}